import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Search, X, RotateCcw, Maximize2, Minimize2, Plus, Minus, MapPin, ExternalLink, Globe, Copy, Check,
  Heart, Eye, Share2, MessageCircle, ShieldCheck, Sparkles, AlertTriangle, Loader2, RefreshCw,
  SlidersHorizontal, Store, Building2, Info, CalendarDays,
} from 'lucide-react';
import '../style/InteractiveMap.css';
import { host } from '../script/variables';

/* ───────────────────────────────────────────────────────────────────────────
   Admin “Global presence” map

   Lightweight by design:
   • OpenStreetMap basemap, muted to a light look via CSS (no API key needed)
   • Individual restaurants are canvas circle markers (no DOM node per pin)
   • Low zoom shows ≤500 server-side cluster bubbles instead of raw pins
   • Viewport requests are debounced and stale requests are aborted
   • Leaflet is loaded on demand if the index.html CDN script isn't ready
   ─────────────────────────────────────────────────────────────────────────── */

const API = `${host}/admin/map`;
const PIN_ZOOM = 11;                              // below → clusters, at/above → pins
const HOME = { center: [22.6, 79.5], zoom: 5 };   // India
// Keyless basemap. CARTO basemaps now require an API key (tiles render as
// "API KEY REQUIRED"), so OpenStreetMap is used. OSM tile policy: light use,
// attribution required — fine for an admin-only map. Swap here if needed.
const TILE = {
  url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors',
  maxZoom: 19,
};
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';

const MENU = {
  veg:       { label: 'Veg',     color: '#16a34a' },
  nonveg:    { label: 'Non-veg', color: '#dc2626' },
  omnivores: { label: 'Both',    color: '#d97706' },
};
const FALLBACK_COLOR = '#64748b';
const WINDOWS = [[6, '6 hours'], [24, '24 hours'], [72, '3 days'], [168, '7 days'], [720, '30 days']];

// ─── helpers ────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n ?? 0).toLocaleString('en-IN');
const compact = (n) => (n >= 10000 ? `${Math.round(n / 1000)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const windowLabel = (h) => (WINDOWS.find(([v]) => v === h)?.[1] || `${h} hours`);
const coordsOf = (c) => {
  const p = c?.location?.coordinates;
  if (!Array.isArray(p) || p.length < 2) return null;
  const [lng, lat] = p.map(Number);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};
const safeUrl = (u) => (typeof u === 'string' && /^https?:\/\//i.test(u) ? u : null);

const debounce = (fn, ms) => {
  let t;
  const d = (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  d.cancel = () => clearTimeout(t);
  return d;
};

const filterParams = ({ menuType, verified }) => {
  const p = new URLSearchParams();
  if (menuType !== 'all') p.set('menuType', menuType);
  if (verified !== 'all') p.set('verified', verified);
  return p;
};

let leafletPromise = null;
const ensureLeaflet = () => {
  if (window.L?.map) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const fail = () => { leafletPromise = null; reject(new Error('The map library could not be loaded. Check your internet connection and retry.')); };
    if (!document.querySelector(`script[src="${LEAFLET_JS}"]`)) {
      const s = document.createElement('script');
      s.src = LEAFLET_JS;
      s.async = true;
      s.onerror = fail;
      document.head.appendChild(s);
    }
    const started = Date.now();
    const poll = () => {
      if (window.L?.map) return resolve(window.L);
      if (Date.now() - started > 12000) return fail();
      setTimeout(poll, 100);
    };
    poll();
  });
  return leafletPromise;
};

const apiGet = async (path, signal) => {
  const res = await fetch(`${API}${path}`, { credentials: 'include', signal });
  if (res.status === 401 || res.status === 403) {
    const e = new Error('Your admin session isn’t reaching the server. Log in again — and make sure your browser isn’t blocking cookies for this site.');
    e.auth = true;
    throw e;
  }
  if (!res.ok) throw new Error(`The server returned an error (${res.status}). Please retry.`);
  return res.json();
};

// ─── KPI strip ──────────────────────────────────────────────────────────────
const Kpi = ({ icon: Icon, label, value, sub, tone }) => (
  <div className={`imx-kpi ${tone ? `imx-kpi--${tone}` : ''}`}>
    <span className="imx-kpi__icon"><Icon size={16} /></span>
    <div className="imx-kpi__body">
      <span className="imx-kpi__value">{value}</span>
      <span className="imx-kpi__label">{label}</span>
      {sub && <span className="imx-kpi__sub">{sub}</span>}
    </div>
  </div>
);

const KpiStrip = ({ stats, error, hours, onRetry }) => {
  if (error) {
    return (
      <div className="imx-kpis imx-kpis--error">
        <AlertTriangle size={16} /> Couldn’t load statistics. <button className="imx-link" onClick={onRetry}>Retry</button>
      </div>
    );
  }
  if (!stats) {
    return <div className="imx-kpis">{[0, 1, 2, 3, 4].map(i => <div key={i} className="imx-kpi imx-kpi--skeleton" />)}</div>;
  }
  const m = stats.byMenuType || {};
  const mixTotal = (m.veg || 0) + (m.nonveg || 0) + (m.omnivores || 0);
  return (
    <div className="imx-kpis">
      <Kpi icon={Store} label="Restaurants" value={fmt(stats.total)} />
      <Kpi icon={Sparkles} label={`New · last ${windowLabel(hours)}`} value={`+${fmt(stats.newInWindow)}`} tone="new" />
      <Kpi icon={ShieldCheck} label="Verified" value={`${pct(stats.verified, stats.total)}%`} sub={`${fmt(stats.verified)} of ${fmt(stats.total)}`} tone="good" />
      <Kpi icon={Building2} label="Locations (approx.)" value={fmt(stats.uniqueCities)} />
      <div className="imx-kpi imx-kpi--mix">
        <span className="imx-kpi__label">Menu mix</span>
        <div className="imx-mix" role="img" aria-label="Menu type distribution">
          {Object.entries(MENU).map(([k, v]) => (
            mixTotal > 0 && m[k] > 0 && <span key={k} style={{ width: `${(m[k] / mixTotal) * 100}%`, background: v.color }} title={`${v.label}: ${fmt(m[k])}`} />
          ))}
        </div>
        <div className="imx-mix__legend">
          {Object.entries(MENU).map(([k, v]) => (
            <span key={k}><i style={{ background: v.color }} />{v.label} {pct(m[k] || 0, mixTotal)}%</span>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Detail drawer ──────────────────────────────────────────────────────────
const Drawer = ({ cafe, hours, onClose, onZoom }) => {
  const [copied, setCopied] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  useEffect(() => { setImgOk(true); setCopied(false); }, [cafe?._id]);

  const c = coordsOf(cafe);
  const menu = MENU[cafe.menuType];
  const website = safeUrl(cafe.website);
  const cats = Array.isArray(cafe.category) ? cafe.category : cafe.category ? [cafe.category] : [];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${c.lat.toFixed(6)}, ${c.lng.toFixed(6)}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };

  return (
    <aside className="imx-drawer" aria-label="Restaurant details">
      <button className="imx-drawer__close" onClick={onClose} aria-label="Close details"><X size={18} /></button>
      {cafe.thumbnail && imgOk ? (
        <div className="imx-drawer__hero"><img src={cafe.thumbnail} alt="" onError={() => setImgOk(false)} /></div>
      ) : (
        <div className="imx-drawer__hero imx-drawer__hero--empty"><Store size={30} /></div>
      )}
      <div className="imx-drawer__body">
        <div className="imx-tags">
          <span className="imx-tag" style={{ '--tag': menu?.color || FALLBACK_COLOR }}>{menu?.label || cafe.menuType || 'Unknown menu'}</span>
          {cafe.isVerified
            ? <span className="imx-tag imx-tag--good"><ShieldCheck size={12} /> Verified</span>
            : <span className="imx-tag imx-tag--muted">Unverified</span>}
          {cafe.isNew && <span className="imx-tag imx-tag--new"><Sparkles size={12} /> New · {windowLabel(hours)}</span>}
        </div>
        <h3 className="imx-drawer__name">{cafe.name || cafe.title || 'Unnamed restaurant'}</h3>
        {cafe.title && cafe.name && cafe.title !== cafe.name && <p className="imx-drawer__sub">{cafe.title}</p>}
        {cafe.startDate && <p className="imx-drawer__meta"><CalendarDays size={13} /> Added {new Date(cafe.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
        {cafe.description && <p className="imx-drawer__desc">{cafe.description}</p>}

        <div className="imx-metrics">
          <div><Heart size={14} /><strong>{fmt(cafe.likes)}</strong><span>Likes</span></div>
          <div><Eye size={14} /><strong>{fmt(cafe.views)}</strong><span>Views</span></div>
          <div><Share2 size={14} /><strong>{fmt(cafe.shares)}</strong><span>Shares</span></div>
          <div><MessageCircle size={14} /><strong>{fmt(cafe.totalComments)}</strong><span>Comments</span></div>
        </div>

        {cats.length > 0 && <div className="imx-cats">{cats.slice(0, 10).map((x, i) => <span key={i}>{x}</span>)}</div>}

        {c && (
          <div className="imx-coords">
            <MapPin size={13} />
            <code>{c.lat.toFixed(5)}, {c.lng.toFixed(5)}</code>
            <button className="imx-icon-btn imx-icon-btn--sm" onClick={copy} aria-label="Copy coordinates" title="Copy coordinates">
              {copied ? <Check size={13} /> : <Copy size={13} />}
            </button>
          </div>
        )}

        <div className="imx-drawer__actions">
          {c && <button className="imx-btn imx-btn--primary" onClick={() => onZoom(cafe)}><MapPin size={15} /> Zoom here</button>}
          {c && (
            <a className="imx-btn" href={`https://www.google.com/maps/search/?api=1&query=${c.lat},${c.lng}`} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={15} /> Google Maps
            </a>
          )}
          {website && <a className="imx-btn" href={website} target="_blank" rel="noopener noreferrer"><Globe size={15} /> Website</a>}
        </div>
      </div>
    </aside>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────
const InteractiveMap = () => {
  const shellRef = useRef(null);
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const rendererRef = useRef(null);
  const layers = useRef(null);
  const req = useRef({ ctrl: null, seq: 0 });
  const filtersRef = useRef({ menuType: 'all', verified: 'all', hours: 24 });
  const searchRef = useRef(null);            // { term, items } while a search is active

  const [mapState, setMapState] = useState('loading');   // loading | ready | error
  const [mapError, setMapError] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const [tileWarning, setTileWarning] = useState(false);

  const [menuType, setMenuType] = useState('all');
  const [verified, setVerified] = useState('all');
  const [hours, setHours] = useState(24);

  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(false);
  const [statsKey, setStatsKey] = useState(0);

  const [view, setView] = useState({ mode: 'clusters', count: 0, total: 0, hasMore: false });
  const [zoom, setZoom] = useState(HOME.zoom);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState(null);

  const [query, setQuery] = useState('');
  const [search, setSearch] = useState(null);             // { term, items, total, loading, error }
  const [selected, setSelected] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);

  // ── drawing ───────────────────────────────────────────────────────────────
  const highlight = useCallback((cafe) => {
    const L = window.L;
    const lay = layers.current;
    if (!L || !lay) return;
    lay.selected.clearLayers();
    const c = coordsOf(cafe);
    if (!c) return;
    L.circleMarker([c.lat, c.lng], {
      renderer: rendererRef.current, radius: 13, color: '#0f172a', weight: 2.5, fill: false, interactive: false,
    }).addTo(lay.selected);
  }, []);

  const selectCafe = useCallback((cafe, { fly = false } = {}) => {
    setSelected(cafe);
    highlight(cafe);
    const c = coordsOf(cafe);
    if (fly && c && mapRef.current) mapRef.current.flyTo([c.lat, c.lng], Math.max(mapRef.current.getZoom(), 15), { duration: 0.7 });
  }, [highlight]);

  const drawPins = useCallback((cafes) => {
    const L = window.L;
    const lay = layers.current;
    if (!L || !lay) return;
    lay.bubbles.clearLayers();
    lay.halo.clearLayers();
    lay.pins.clearLayers();
    const renderer = rendererRef.current;
    cafes.forEach((cafe) => {
      const c = coordsOf(cafe);
      if (!c) return;
      const color = MENU[cafe.menuType]?.color || FALLBACK_COLOR;
      if (cafe.isNew) {
        L.circleMarker([c.lat, c.lng], { renderer, radius: 13, stroke: false, fillColor: color, fillOpacity: 0.2, interactive: false }).addTo(lay.halo);
      }
      L.circleMarker([c.lat, c.lng], {
        renderer,
        radius: cafe.isNew ? 7 : 6,
        color: '#ffffff',
        weight: 2,
        fillColor: color,
        fillOpacity: cafe.isVerified ? 1 : 0.5,
      })
        .bindTooltip(
          `<strong>${esc(cafe.name || cafe.title || 'Unnamed')}</strong><span>${esc(MENU[cafe.menuType]?.label || 'Unknown')} · ${cafe.isVerified ? 'Verified' : 'Unverified'}${cafe.isNew ? ' · New' : ''}</span>`,
          { direction: 'top', offset: [0, -6], className: 'imx-tooltip' }
        )
        .on('click', () => selectCafe(cafe))
        .addTo(lay.pins);
    });
  }, [selectCafe]);

  const drawClusters = useCallback((clusters) => {
    const L = window.L;
    const lay = layers.current;
    const map = mapRef.current;
    if (!L || !lay || !map) return;
    lay.pins.clearLayers();
    lay.halo.clearLayers();
    lay.bubbles.clearLayers();
    const max = Math.max(1, ...clusters.map(c => c.count));
    clusters.forEach((cl) => {
      const size = Math.round(30 + 30 * Math.sqrt(cl.count / max));
      const s = cl.menuTypeSplit || {};
      const tot = (s.veg || 0) + (s.nonveg || 0) + (s.omnivores || 0) || 1;
      const a = ((s.veg || 0) / tot) * 360;
      const b = a + ((s.nonveg || 0) / tot) * 360;
      const ring = `conic-gradient(${MENU.veg.color} 0deg ${a}deg, ${MENU.nonveg.color} ${a}deg ${b}deg, ${MENU.omnivores.color} ${b}deg 360deg)`;
      const icon = L.divIcon({
        className: 'imx-bubble-icon',
        html: `<div class="imx-bubble" style="width:${size}px;height:${size}px;background:${ring}"><span>${compact(cl.count)}</span></div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
      L.marker([cl.lat, cl.lng], { icon, keyboard: false, riseOnHover: true })
        .bindTooltip(
          `<strong>${fmt(cl.count)} restaurants</strong>
           <span><i style="background:${MENU.veg.color}"></i>Veg ${fmt(s.veg)} &nbsp;<i style="background:${MENU.nonveg.color}"></i>Non-veg ${fmt(s.nonveg)} &nbsp;<i style="background:${MENU.omnivores.color}"></i>Both ${fmt(s.omnivores)}</span>
           ${cl.verified != null ? `<span>${fmt(cl.verified)} verified</span>` : ''}
           <em>Click to zoom in</em>`,
          { direction: 'top', offset: [0, -size / 2], className: 'imx-tooltip' }
        )
        .on('click', () => map.flyTo([cl.lat, cl.lng], Math.min(map.getZoom() + 3, PIN_ZOOM), { duration: 0.7 }))
        .addTo(lay.bubbles);
    });
  }, []);

  // ── data ──────────────────────────────────────────────────────────────────
  const loadViewport = useCallback(async () => {
    const map = mapRef.current;
    if (!map) return;

    req.current.ctrl?.abort();
    const ctrl = new AbortController();
    req.current.ctrl = ctrl;
    const seq = ++req.current.seq;

    // Active search → show its result pins regardless of zoom
    if (searchRef.current) {
      drawPins(searchRef.current.items);
      setView({ mode: 'search', count: searchRef.current.items.length, total: searchRef.current.total, hasMore: false });
      setLoading(false);
      return;
    }

    const z = map.getZoom();
    const b = map.getBounds();
    const params = filterParams(filtersRef.current);
    params.set('bbox', [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].map(v => v.toFixed(5)).join(','));
    params.set('zoom', z);

    setLoading(true);
    setDataError(null);
    try {
      if (z < PIN_ZOOM) {
        const json = await apiGet(`/clusters?${params}`, ctrl.signal);
        if (seq !== req.current.seq) return;
        const clusters = json.data || [];
        drawClusters(clusters);
        const sum = clusters.reduce((acc, c) => acc + c.count, 0);
        setView({ mode: 'clusters', count: sum, total: sum, hasMore: false });
      } else {
        params.set('hours', filtersRef.current.hours);
        const json = await apiGet(`/cafes?${params}`, ctrl.signal);
        if (seq !== req.current.seq) return;
        const cafes = json.data || [];
        drawPins(cafes);
        setView({ mode: 'pins', count: cafes.length, total: json.total ?? cafes.length, hasMore: !!json.hasMore });
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      if (seq === req.current.seq) setDataError(e);
    } finally {
      if (seq === req.current.seq) setLoading(false);
    }
  }, [drawClusters, drawPins]);

  // ── map init ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    let resizeObserver;
    let onMove;
    const requests = req.current;
    setMapState('loading');

    ensureLeaflet()
      .then((L) => {
        if (cancelled || !mapEl.current || mapRef.current) return;
        const map = L.map(mapEl.current, {
          center: HOME.center,
          zoom: HOME.zoom,
          minZoom: 3,
          maxZoom: 18,
          zoomControl: false,
          worldCopyJump: true,
        });
        map.attributionControl.setPrefix(false);

        const tiles = L.tileLayer(TILE.url, {
          attribution: TILE.attribution,
          maxZoom: TILE.maxZoom,
          updateWhenIdle: true,
          keepBuffer: 2,
          crossOrigin: true,
        });
        let tileErrors = 0;
        tiles.on('tileerror', () => { tileErrors += 1; if (tileErrors === 6) setTileWarning(true); });
        tiles.on('load', () => { tileErrors = 0; setTileWarning(false); });
        tiles.addTo(map);

        rendererRef.current = L.canvas({ padding: 0.5, tolerance: 6 });
        layers.current = {
          bubbles: L.layerGroup().addTo(map),
          halo: L.layerGroup().addTo(map),
          pins: L.layerGroup().addTo(map),
          selected: L.layerGroup().addTo(map),
        };
        mapRef.current = map;

        onMove = debounce(() => { setZoom(map.getZoom()); loadViewport(); }, 300);
        map.on('moveend', onMove);           // also fires after zoom

        // Keep Leaflet in sync with container size (sidebar, fullscreen, tab animations)
        resizeObserver = new ResizeObserver(() => map.invalidateSize({ pan: false }));
        resizeObserver.observe(mapEl.current);

        setZoom(map.getZoom());
        setMapState('ready');
        loadViewport();
      })
      .catch((err) => {
        if (cancelled) return;
        setMapError(err.message);
        setMapState('error');
      });

    return () => {
      cancelled = true;
      onMove?.cancel();
      resizeObserver?.disconnect();
      requests.ctrl?.abort();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        layers.current = null;
      }
    };
  }, [loadViewport, retryKey]);

  // ── filters ───────────────────────────────────────────────────────────────
  const runSearch = useCallback(async (term) => {
    const t = term.trim();
    if (!t) return;
    const params = filterParams(filtersRef.current);
    params.set('search', t);
    params.set('zoom', 9);                     // server limit: up to 800 matches
    params.set('hours', filtersRef.current.hours);
    setSearch({ term: t, items: [], total: 0, loading: true, error: null });
    try {
      const json = await apiGet(`/cafes?${params}`);
      const items = (json.data || []).filter(c => coordsOf(c));
      searchRef.current = { term: t, items, total: json.total ?? items.length };
      setSearch({ term: t, items, total: json.total ?? items.length, loading: false, error: null });
      const L = window.L;
      const map = mapRef.current;
      if (map && L && items.length) {
        const bounds = L.latLngBounds(items.map(c => { const p = coordsOf(c); return [p.lat, p.lng]; }));
        map.fitBounds(bounds, { padding: [60, 60], maxZoom: 14 });
      }
      loadViewport();
    } catch (e) {
      setSearch({ term: t, items: [], total: 0, loading: false, error: e.message });
    }
  }, [loadViewport]);

  const clearSearch = useCallback(() => {
    searchRef.current = null;
    setSearch(null);
    setQuery('');
    loadViewport();
  }, [loadViewport]);

  useEffect(() => {
    filtersRef.current = { menuType, verified, hours };
    if (mapState !== 'ready') return;
    if (searchRef.current) runSearch(searchRef.current.term);
    else loadViewport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuType, verified, hours]);

  // ── stats ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const ctrl = new AbortController();
    setStatsError(false);
    apiGet(`/stats?hours=${hours}`, ctrl.signal)
      .then(json => setStats(json.data))
      .catch(e => { if (e.name !== 'AbortError') setStatsError(true); });
    return () => ctrl.abort();
  }, [hours, statsKey]);

  // ── UI helpers ────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { setSelected(null); layers.current?.selected.clearLayers(); } };
    const onFs = () => setFullscreen(document.fullscreenElement === shellRef.current);
    window.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFs);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('fullscreenchange', onFs); };
  }, []);

  const toggleFullscreen = async () => {
    const el = shellRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (el.requestFullscreen) await el.requestFullscreen();
      else setFullscreen(f => !f);           // CSS fallback
    } catch { setFullscreen(f => !f); }
  };

  const resetView = () => mapRef.current?.flyTo(HOME.center, HOME.zoom, { duration: 0.8 });
  const refresh = () => { setStatsKey(k => k + 1); loadViewport(); };
  const closeDrawer = () => { setSelected(null); layers.current?.selected.clearLayers(); };

  const statusText = (() => {
    if (view.mode === 'search') return `${fmt(view.count)}${view.total > view.count ? ` of ${fmt(view.total)}` : ''} match${view.total === 1 ? '' : 'es'}`;
    if (view.mode === 'clusters') return `${fmt(view.count)} restaurants in view`;
    if (view.hasMore) return `Showing ${fmt(view.count)} of ${fmt(view.total)} — zoom in for all`;
    return `${fmt(view.count)} restaurant${view.count === 1 ? '' : 's'} in view`;
  })();

  const filtersActive = menuType !== 'all' || verified !== 'all';

  return (
    <div className="imx-root">
      <KpiStrip stats={stats} error={statsError} hours={hours} onRetry={() => setStatsKey(k => k + 1)} />

      <div ref={shellRef} className={`imx-shell ${fullscreen ? 'imx-shell--fullscreen' : ''}`}>
        <div ref={mapEl} className="imx-map" />

        {mapState === 'loading' && (
          <div className="imx-overlay"><Loader2 size={26} className="imx-spin" /><span>Loading map…</span></div>
        )}
        {mapState === 'error' && (
          <div className="imx-overlay">
            <AlertTriangle size={28} />
            <strong>Map couldn’t load</strong>
            <span>{mapError}</span>
            <button className="imx-btn imx-btn--primary" onClick={() => setRetryKey(k => k + 1)}><RefreshCw size={15} /> Retry</button>
          </div>
        )}

        {mapState === 'ready' && (
          <>
            {/* ── Control panel ── */}
            <section className={`imx-panel ${panelOpen ? '' : 'imx-panel--collapsed'}`} aria-label="Map filters">
              <form className="imx-search" onSubmit={(e) => { e.preventDefault(); runSearch(query); }}>
                <Search size={16} className="imx-search__icon" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search restaurants by name…"
                  aria-label="Search restaurants"
                />
                {(query || search) && (
                  <button type="button" className="imx-icon-btn imx-icon-btn--sm" onClick={clearSearch} aria-label="Clear search"><X size={14} /></button>
                )}
                <button type="button" className={`imx-icon-btn imx-icon-btn--sm imx-panel__toggle ${filtersActive ? 'imx-dot' : ''}`} onClick={() => setPanelOpen(o => !o)} aria-label={panelOpen ? 'Hide filters' : 'Show filters'} aria-expanded={panelOpen}>
                  <SlidersHorizontal size={15} />
                </button>
              </form>

              {search && (
                <div className="imx-results">
                  {search.loading && <div className="imx-results__state"><Loader2 size={14} className="imx-spin" /> Searching “{search.term}”…</div>}
                  {search.error && <div className="imx-results__state imx-results__state--bad">{search.error}</div>}
                  {!search.loading && !search.error && search.items.length === 0 && (
                    <div className="imx-results__state">No restaurants match “{search.term}”{filtersActive ? ' with the current filters' : ''}.</div>
                  )}
                  {!search.loading && search.items.length > 0 && (
                    <>
                      <div className="imx-results__head">
                        {fmt(search.items.length)}{search.total > search.items.length ? ` of ${fmt(search.total)}` : ''} result{search.total === 1 ? '' : 's'}
                      </div>
                      <ul>
                        {search.items.slice(0, 50).map((c) => (
                          <li key={c._id}>
                            <button className={selected?._id === c._id ? 'is-active' : ''} onClick={() => selectCafe(c, { fly: true })}>
                              <i style={{ background: MENU[c.menuType]?.color || FALLBACK_COLOR }} />
                              <span>{c.name || c.title || 'Unnamed'}</span>
                              {c.isVerified && <ShieldCheck size={13} className="imx-ok" />}
                            </button>
                          </li>
                        ))}
                      </ul>
                      {search.items.length > 50 && <div className="imx-results__state">Showing first 50 here — all {fmt(search.items.length)} are on the map.</div>}
                    </>
                  )}
                </div>
              )}

              {panelOpen && (
                <div className="imx-filters">
                  <div className="imx-field">
                    <span className="imx-field__label">Menu type</span>
                    <div className="imx-seg">
                      <button className={menuType === 'all' ? 'is-active' : ''} onClick={() => setMenuType('all')}>All</button>
                      {Object.entries(MENU).map(([k, v]) => (
                        <button key={k} className={menuType === k ? 'is-active' : ''} onClick={() => setMenuType(k)}>
                          <i style={{ background: v.color }} />{v.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="imx-field">
                    <span className="imx-field__label">Verification</span>
                    <div className="imx-seg">
                      {[['all', 'All'], ['true', 'Verified'], ['false', 'Unverified']].map(([v, l]) => (
                        <button key={v} className={verified === v ? 'is-active' : ''} onClick={() => setVerified(v)}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div className="imx-field">
                    <label className="imx-field__label" htmlFor="imx-window">Highlight restaurants added in the last</label>
                    <select id="imx-window" className="imx-select" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
                      {WINDOWS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  {filtersActive && (
                    <button className="imx-link" onClick={() => { setMenuType('all'); setVerified('all'); }}>Reset filters</button>
                  )}
                </div>
              )}
            </section>

            {/* ── Status ── */}
            <div className="imx-status" aria-live="polite">
              {loading ? <Loader2 size={14} className="imx-spin" /> : <span className={`imx-status__dot ${view.hasMore ? 'is-warn' : ''}`} />}
              <span>{statusText}</span>
            </div>

            {/* ── Map tools ── */}
            <div className="imx-tools">
              <button className="imx-icon-btn" onClick={() => mapRef.current?.zoomIn()} aria-label="Zoom in" title="Zoom in"><Plus size={17} /></button>
              <button className="imx-icon-btn" onClick={() => mapRef.current?.zoomOut()} aria-label="Zoom out" title="Zoom out"><Minus size={17} /></button>
              <span className="imx-tools__sep" />
              <button className="imx-icon-btn" onClick={resetView} aria-label="Reset view" title="Reset to India"><RotateCcw size={16} /></button>
              <button className="imx-icon-btn" onClick={refresh} aria-label="Refresh data" title="Refresh"><RefreshCw size={16} /></button>
              <button className="imx-icon-btn" onClick={toggleFullscreen} aria-label={fullscreen ? 'Exit full screen' : 'Full screen'} title={fullscreen ? 'Exit full screen' : 'Full screen'}>
                {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
            </div>

            {/* ── Legend ── */}
            <div className="imx-legend">
              {Object.values(MENU).map(v => <span key={v.label}><i style={{ background: v.color }} />{v.label}</span>)}
              <span><i className="imx-legend__new" />New</span>
              <span><i className="imx-legend__faded" />Unverified</span>
            </div>

            {/* ── Hints & errors ── */}
            {view.mode === 'clusters' && !dataError && (
              <div className="imx-hint"><Info size={14} /> Click a bubble or zoom in to see individual restaurants</div>
            )}
            {view.mode === 'pins' && !loading && !dataError && view.count === 0 && (
              <div className="imx-hint">No restaurants here{filtersActive ? ' match these filters' : ''}. Try zooming out.</div>
            )}
            {dataError && (
              <div className="imx-error" role="alert">
                <AlertTriangle size={16} />
                <span>{dataError.message}</span>
                {!dataError.auth && <button className="imx-link" onClick={loadViewport}>Retry</button>}
              </div>
            )}
            {tileWarning && !dataError && (
              <div className="imx-error imx-error--soft"><AlertTriangle size={15} /> Some map tiles failed to load — check your connection.</div>
            )}

            {selected && <Drawer cafe={selected} hours={hours} onClose={closeDrawer} onZoom={(c) => selectCafe(c, { fly: true })} />}
          </>
        )}

        {zoom >= PIN_ZOOM && mapState === 'ready' && <span className="imx-sr">Showing individual restaurants</span>}
      </div>
    </div>
  );
};

export default InteractiveMap;
